import { TextFieldType } from "@material/web/textfield/internal/text-field";
import { LitOutlinedTextField, LitFilledTextField } from "./LitTextField";
import { SlotIconButton, SlotIconName } from "../Icon/SlotIcon";
import { forwardRef, FocusEvent, KeyboardEvent, useState } from "react";

const variants = {
  outlined: LitOutlinedTextField,
  filled: LitFilledTextField
};

export interface TextFieldProps {
  className?: string;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  label?: string;
  focusedLabel?: string; // Optional different label when focused
  leadingIcon?: SlotIconName;
  name?: string;
  onChange?(e: Event): void;
  onChangeText?(v: string): void;
  placeholder?: string;
  required?: boolean;
  trailingIcon?: SlotIconName;
  type?: TextFieldType;
  value?: string;
  variant?: keyof typeof variants;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  rows?: number;
  tabIndex?: number;
  readOnly?: boolean;
  supportingText?: string;
  step?: string | number;
  min?: string | number;
}

export default forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  {
    variant = "outlined",
    disabled = false,
    label,
    focusedLabel,
    required,
    value = "",
    leadingIcon,
    trailingIcon,
    onChange,
    onChangeText,
    onFocus,
    onBlur,
    onKeyDown,
    placeholder,
    step,
    min,
    ...props
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const TextFieldComponent = variants[variant];

  const handleFocus = (e: FocusEvent<HTMLElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  // Use focusedLabel when focused and available, otherwise use regular label
  const currentLabel = isFocused && focusedLabel ? focusedLabel : label;
  const displayLabel = required ? `${currentLabel} *` : currentLabel;

  return (
    <TextFieldComponent
      onChange={(e: Event) => {
        onChange?.(e);
        onChangeText?.((e.target as HTMLInputElement).value);
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      // @lit/react event maps type listeners as Event; callers keep KeyboardEvent for DX.
      onKeyDown={onKeyDown as ((event: Event) => void) | undefined}
      hasLeadingIcon={!!leadingIcon}
      hasTrailingIcon={!!trailingIcon}
      label={displayLabel}
      aria-label={displayLabel ?? placeholder}
      placeholder={placeholder}
      ref={ref as any}
      {...{ value, disabled }}
      {...props}
      step={step?.toString()}
      min={min?.toString()}
    >
      {leadingIcon && <SlotIconButton name={leadingIcon} slot="leading-icon" />}
      {trailingIcon && <SlotIconButton name={trailingIcon} slot="trailing-icon" />}
    </TextFieldComponent>
  );
});
